import { useIntersection } from '@mantine/hooks'
import {
  type CSSProperties,
  memo,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { FileTrigger as AriaFileTrigger } from 'react-aria-components'
import { useDebounce } from 'react-use'
import { VirtuosoGrid } from 'react-virtuoso'
import { useImmer } from 'use-immer'
import FluentCheckmarkCircle32Filled from '~icons/fluent/checkmark-circle-32-filled'
import FluentDismiss24Filled from '~icons/fluent/dismiss-24-filled'
import type { Media } from 'src/services/apollo/generated/graphql'
import { cx } from 'src/utils/cx'
import { UIButton } from '../../ui/Button'
import {
  UIDialogContent,
  UIDialogFooter,
  UIDialogHeader,
  UIDialogResponsive,
} from '../../ui/Modal'
import Text from '../../ui/Text'
import MediaPreview from '../MediaPreview'

const DefaultMediaItem = memo(
  <T extends Media>({
    item,
    isSelected,
    onSelect,
  }: {
    item: T
    isSelected: boolean
    onSelect: (id: string) => void
  }) => {
    const handleClick = useCallback(() => {
      onSelect(item.id)
    }, [item.id, onSelect])

    return (
      <div
        className={cx(
          'relative transition cursor-pointer',
          isSelected && 'scale-90',
        )}
        onClick={handleClick}
      >
        <div className={cx(isSelected && 'opacity-50')}>
          <MediaPreview
            mediaId={item.id}
            thumbnail
            preferStill
            className="w-full aspect-square rounded ring-1 ring-black/10 dark:ring-black/20"
          />
        </div>
        {isSelected && (
          <FluentCheckmarkCircle32Filled className="absolute inset-0 m-auto size-8 text-purple-600 bg-black rounded-full" />
        )}
      </div>
    )
  },
)
DefaultMediaItem.displayName = 'DefaultMediaItem'

const LoadingPlaceholder = memo(({ opacity }: { opacity: number }) => (
  <div className="w-full aspect-square" style={{ opacity }}>
    <div className="size-full bg-black/20 dark:bg-white/20 rounded animate-pulse" />
  </div>
))
LoadingPlaceholder.displayName = 'LoadingPlaceholder'

export type MediaPickerRenderItemState<T extends Media> = {
  item: T
  isSelected: boolean
  onSelect: (id: string) => void
}

export type MediaPickerDataSource<T extends Media = Media> = {
  items: T[]
  loading: boolean
  loadingMore: boolean
  hasMore: boolean
  fetchMore: () => void | Promise<void>
}

export type MediaPickerProps<
  M extends boolean = false,
  T extends Media = Media,
  V extends T | T[] = M extends true ? T[] : T,
> = {
  // Dialog state
  open: boolean
  onClose?: () => void

  // Selection mode
  multiple?: M
  onSubmit?: (value: V) => void

  // Data source
  dataSource: MediaPickerDataSource<T>

  // Render props
  renderItem?: (state: MediaPickerRenderItemState<T>) => ReactNode
  renderSelectedPreview?: (item: T, onRemove: () => void) => ReactNode

  // Layout
  itemWidth?: number | string
  gapSize?: number

  // File upload
  showFileUpload?: boolean
  acceptedFileTypes?: string[]
  onSelectedFiles?: (files: File[]) => void

  // Text
  title?: string

  // Additional props
  dialogClassName?: string
  contentClassName?: string
}

export function MediaPicker<
  M extends boolean = false,
  T extends Media = Media,
  V extends T | T[] = M extends true ? T[] : T,
>(props: MediaPickerProps<M, T, V>) {
  const {
    open,
    onClose,
    multiple,
    onSubmit,
    dataSource,
    renderItem: customRenderItem,
    renderSelectedPreview,
    itemWidth = 120,
    gapSize = 4,
    showFileUpload = false,
    acceptedFileTypes = ['image/jpeg', 'image/png'],
    title,
    onSelectedFiles,
    dialogClassName,
    contentClassName,
  } = props

  // Selection state
  const [selected, updateSelected] = useImmer(() => new Set<string>())

  // Reset selection when dialog closes
  useEffect(() => {
    if (!open) {
      updateSelected(new Set())
    }
  }, [open, updateSelected])

  // Handle item selection
  const onSelect = useCallback(
    (itemId: string) => {
      if (multiple) {
        updateSelected(selected => {
          if (selected.has(itemId)) {
            selected.delete(itemId)
          } else {
            selected.add(itemId)
          }
        })
      } else {
        const item = dataSource.items.find(item => item.id === itemId)
        if (item) {
          onSubmit?.(item as V)
        }
        onClose?.()
      }
    },
    [multiple, dataSource.items, onSubmit, onClose, updateSelected],
  )

  // Handle confirm (for multiple selection)
  const handleConfirm = useCallback(() => {
    if (multiple) {
      const selectedItems = dataSource.items.filter(item =>
        selected.has(item.id),
      )
      onSubmit?.(selectedItems as V)
    }
    onClose?.()
  }, [multiple, dataSource.items, selected, onSubmit, onClose])

  // Default item renderer wrapped in useCallback
  const defaultRenderItem = useCallback(
    (state: MediaPickerRenderItemState<T>) => (
      <DefaultMediaItem
        item={state.item}
        isSelected={state.isSelected}
        onSelect={state.onSelect}
      />
    ),
    [],
  )

  // Use custom or default renderer
  const renderItem = customRenderItem ?? defaultRenderItem

  // Get selected items for preview
  const selectedItems = useMemo(() => {
    return dataSource.items.filter(item => selected.has(item.id))
  }, [dataSource.items, selected])

  return (
    <UIDialogResponsive
      open={open}
      onClose={onClose}
      className={cx(
        'flex flex-col mmd:w-[796px] h-[calc(var(--visible-height)-80px)]',
        dialogClassName,
      )}
      dismissable
    >
      <UIDialogHeader
        heading={
          title ?? (
            <Text i18nKey="action:image-source.media-picker.default-title" />
          )
        }
      />
      <UIDialogContent className={cx('flex-1 flex flex-col', contentClassName)}>
        {/* Media grid */}
        <section className="flex-1 overflow-hidden">
          <MediaGrid
            dataSource={dataSource}
            selected={selected}
            onSelect={onSelect}
            renderItem={renderItem}
            itemWidth={itemWidth}
            gapSize={gapSize}
          />
        </section>

        {/* Selected items preview (for multiple selection) */}
        {multiple && (
          <section className="flex-none mt-4 flex gap-2 h-12 overflow-x-auto">
            {/* Selected count / clear button */}
            <button
              className="group sticky left-0 grid place-content-center h-full aspect-square bg-background-light rounded"
              onClick={() => {
                updateSelected(selected => {
                  selected.clear()
                })
              }}
            >
              <div className="contents group-hover:hidden">
                <span>{selected.size}</span>
              </div>
              <FluentDismiss24Filled className="size-6 hidden group-hover:block" />
            </button>

            {/* Selected items */}
            {selectedItems.map(item => {
              const handleRemove = () => {
                updateSelected(selected => {
                  selected.delete(item.id)
                })
              }

              // Use custom preview renderer if provided
              if (renderSelectedPreview) {
                return (
                  <div key={item.id}>
                    {renderSelectedPreview(item, handleRemove)}
                  </div>
                )
              }

              // Default preview renderer
              return (
                <button
                  key={item.id}
                  className="group flex-none relative h-full aspect-square overflow-hidden rounded"
                  onClick={handleRemove}
                >
                  <MediaPreview
                    mediaId={item.id}
                    thumbnail
                    preferStill
                    className="size-full"
                  />
                  <div className="absolute inset-0 grid place-content-center bg-black/50 invisible group-hover:visible">
                    <FluentDismiss24Filled className="size-6 text-white" />
                  </div>
                </button>
              )
            })}
          </section>
        )}
      </UIDialogContent>

      <UIDialogFooter className="flex gap-2 msm:justify-start">
        {/* File upload button */}
        {showFileUpload && onSelectedFiles && (
          <AriaFileTrigger
            acceptedFileTypes={acceptedFileTypes}
            allowsMultiple={multiple}
            onSelect={async files => {
              if (files) {
                onSelectedFiles([...files])
              }
            }}
          >
            <UIButton variant="outlined" className="capitalize">
              <Text i18nKey="action:image-source.from-upload" />
            </UIButton>
          </AriaFileTrigger>
        )}

        {/* Cancel button */}
        <UIButton className="capitalize" variant="outlined" onPress={onClose}>
          <Text i18nKey="action:cancel" />
        </UIButton>

        {/* Confirm button (for multiple selection) */}
        {multiple && (
          <UIButton
            variant="filled"
            className="capitalize"
            onPress={handleConfirm}
          >
            <Text i18nKey="action:confirm" />
          </UIButton>
        )}
      </UIDialogFooter>
    </UIDialogResponsive>
  )
}

/**
 * Internal grid component with infinite scrolling support
 */
function MediaGrid<T extends Media>(props: {
  dataSource: MediaPickerDataSource<T>
  selected: Set<string>
  onSelect: (id: string) => void
  renderItem: (state: MediaPickerRenderItemState<T>) => ReactNode
  itemWidth: number | string
  gapSize: number
}) {
  const { dataSource, selected, onSelect, renderItem, itemWidth, gapSize } =
    props

  const { items, loading, loadingMore, hasMore, fetchMore } = dataSource

  // Add placeholder items when loading more
  const displayItems = useMemo(() => {
    const result = [...items]
    if (hasMore && (loading || loadingMore)) {
      result.push(
        ...Array.from({ length: 3 }).map(
          (_, i) =>
            ({
              id: `loading-${i}`,
              __loading: true,
            }) as unknown as T,
        ),
      )
    }
    return result
  }, [items, hasMore, loading, loadingMore])

  // Infinite scroll setup
  const [scroller, setScroller] = useState<HTMLElement | null>(null)
  const { ref: setFooter, entry: intersection } = useIntersection({
    root: scroller,
  })

  useDebounce(
    () => {
      if (!loading && !loadingMore && hasMore && intersection?.isIntersecting) {
        fetchMore()
      }
    },
    100,
    [intersection?.isIntersecting, loading, loadingMore, hasMore, fetchMore],
  )

  // Memoize context to prevent unnecessary re-renders
  const context = useMemo(
    () => ({
      hasMore,
      empty: !loading && items.length === 0,
      setFooter,
    }),
    [hasMore, loading, items.length, setFooter],
  )

  // Memoize item content renderer
  const itemContent = useCallback(
    (idx: number, item: T) => {
      // Loading placeholder
      if ('__loading' in item && item.__loading) {
        const opacity = 1 - (1 / 3) * (3 - (displayItems.length - idx))
        return <LoadingPlaceholder opacity={opacity} />
      }

      // Regular item
      const isSelected = selected.has(item.id)
      return renderItem({ item, isSelected, onSelect })
    },
    [displayItems.length, selected, renderItem, onSelect],
  )

  return (
    <div
      className="h-full"
      style={
        {
          '--item-width':
            typeof itemWidth === 'number' ? itemWidth + 'px' : itemWidth,
          '--gap-size': gapSize + 'px',
        } as CSSProperties
      }
    >
      <VirtuosoGrid
        scrollerRef={setScroller}
        data={displayItems}
        context={context}
        itemContent={itemContent}
        listClassName="grid grid-cols-[repeat(auto-fill,var(--item-width))] gap-[--gap-size] mx-[--gap-size] justify-center"
        components={{
          Footer: MediaGridFooter,
        }}
      />
    </div>
  )
}

/**
 * Footer component for the grid
 */
function MediaGridFooter(props: {
  context?: {
    hasMore: boolean
    empty: boolean
    setFooter: (el: HTMLElement | null) => void
  }
}) {
  const { hasMore, empty } = props.context ?? {}

  return (
    <div
      ref={props.context?.setFooter}
      className="pb-[--gap-size] text-sm text-center text-neutral-500 grid grid-cols-[repeat(auto-fill,var(--item-width))] gap-[--gap-size] mx-[--gap-size] justify-center"
    >
      <div className="row-start-1 col-start-1 w-full aspect-square" />
      <div className="row-start-1 col-start-1 col-span-full flex justify-center items-center">
        {hasMore ? (
          <Text i18nKey="common:hint.loading" />
        ) : empty ? (
          <Text i18nKey="artwork:search-results.empty-without-keyword" />
        ) : (
          <Text i18nKey="common:list-reached-the-end" />
        )}
      </div>
    </div>
  )
}

export default MediaPicker
